import * as THREE from "three";
import { scene } from "../viewer/scene";

/**
 * Координатная сетка и фон 3D-пространства.
 *
 * Сделано лаконично «в стиле Autodesk»: две сетки (мелкая 1 м + акцентная 8 м),
 * мягко затухающие к краям, чтобы не перегружать сцену и не мешать чтению модели.
 * Сетка по умолчанию скрыта и переключается в настройках.
 */

const GRID_STORAGE_KEY = "ifc-viewer.grid-visible";

const FADE_START = 20;
const FADE_END = 46;

interface GridPalette {
  background: number;
  minor: number;
  minorOpacity: number;
  major: number;
  majorOpacity: number;
}

const PALETTE: Record<"dark" | "light", GridPalette> = {
  // Светлая тема: спокойный холодно-серый фон, неяркая сетка.
  light: {
    background: 0xeef1f5,
    minor: 0xc2c8d2,
    minorOpacity: 0.5,
    major: 0x9aa3b2,
    majorOpacity: 0.8,
  },
  // Тёмная тема: мягкий тёмный фон (не «чёрная дыра»), деликатная сетка.
  dark: {
    background: 0x16181d,
    minor: 0x2c3038,
    minorOpacity: 0.65,
    major: 0x3c424d,
    majorOpacity: 0.95,
  },
};

let gridGroup: THREE.Group | null = null;
let minorGrid: THREE.GridHelper | null = null;
let majorGrid: THREE.GridHelper | null = null;

/**
 * Дописывает в материал линий радиальное затухание по расстоянию от центра в
 * плоскости XZ — край сетки растворяется в фоне (как «бесконечный пол»).
 * Делаем устойчиво: если в шейдере нет ожидаемых токенов, просто выходим.
 */
function addGridFade(material: THREE.LineBasicMaterial): void {
  material.transparent = true;
  material.depthWrite = false;
  material.vertexColors = false;
  material.onBeforeCompile = (shader) => {
    if (
      !shader.vertexShader.includes("#include <begin_vertex>") ||
      !shader.fragmentShader.includes("#include <opaque_fragment>")
    ) {
      return;
    }
    shader.uniforms.uFadeStart = { value: FADE_START };
    shader.uniforms.uFadeEnd = { value: FADE_END };
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vGridWorld;")
      .replace(
        "#include <begin_vertex>",
        "#include <begin_vertex>\n  vGridWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;",
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nvarying vec3 vGridWorld;\nuniform float uFadeStart;\nuniform float uFadeEnd;",
      )
      .replace(
        "#include <opaque_fragment>",
        "#include <opaque_fragment>\n  {\n    float gridDist = length(vGridWorld.xz);\n    float gridFade = 1.0 - smoothstep(uFadeStart, uFadeEnd, gridDist);\n    gl_FragColor.a *= gridFade;\n  }",
      );
  };
  material.needsUpdate = true;
}

function readGridVisible(): boolean {
  // По умолчанию (ключ отсутствует) сетка скрыта.
  return localStorage.getItem(GRID_STORAGE_KEY) === "1";
}

/** Создаёт группу координатной сетки (мелкая + акцентная) и оси. */
export function createSceneGrid(): THREE.Group {
  gridGroup = new THREE.Group();
  gridGroup.name = "coord-grid";

  minorGrid = new THREE.GridHelper(96, 96); // ячейка 1 м
  majorGrid = new THREE.GridHelper(96, 12); // акцент каждые 8 м
  majorGrid.position.y = 0.002; // чуть выше — без z-fighting с мелкой сеткой

  for (const helper of [minorGrid, majorGrid]) {
    helper.renderOrder = -1;
    const mat = helper.material as THREE.LineBasicMaterial | THREE.LineBasicMaterial[];
    if (Array.isArray(mat)) {
      mat.forEach((m) => addGridFade(m));
    } else {
      addGridFade(mat);
    }
  }

  gridGroup.add(minorGrid, majorGrid);
  gridGroup.visible = readGridVisible();
  return gridGroup;
}

function paintGrid(helper: THREE.GridHelper | null, color: number, opacity: number): void {
  if (!helper) {
    return;
  }
  const apply = (m: THREE.LineBasicMaterial) => {
    m.color.set(color);
    m.opacity = opacity;
    m.transparent = true;
    m.needsUpdate = true;
  };
  const mat = helper.material as THREE.LineBasicMaterial | THREE.LineBasicMaterial[];
  if (Array.isArray(mat)) {
    mat.forEach(apply);
  } else {
    apply(mat);
  }
}

export function applySceneTheme(effective: "dark" | "light"): void {
  const palette = PALETTE[effective];
  scene.background = new THREE.Color(palette.background);
  paintGrid(minorGrid, palette.minor, palette.minorOpacity);
  paintGrid(majorGrid, palette.major, palette.majorOpacity);
}

export function isGridVisible(): boolean {
  return gridGroup?.visible ?? false;
}

export function setGridVisible(visible: boolean): void {
  if (gridGroup) {
    gridGroup.visible = visible;
  }
  localStorage.setItem(GRID_STORAGE_KEY, visible ? "1" : "0");
}
